import { logger } from "@justhome/common";
import {
  LanguageISOCode,
  Lead,
  LeadCreationAction,
  MaximumBudget,
  usePostLead,
  usePostMaximumBudget,
} from "@justhome/common/api/core";
import { useCallback, useEffect, useRef, useState } from "react";
import mapFormToMaximumBudget from "../budgetCheck/mapFormToMaximumBudget";
import { BudgetCheckFormValues, YesNo } from "../budgetCheck/types";
import { Locale } from "../types";
import { getOrigin } from "../utm";

interface UsePostApplicationWithLeadProps {
  locale: Locale;
  onSuccess: (maximumBudget: MaximumBudget, lead: Lead) => void;
  budgetCheckForm?: BudgetCheckFormValues;
  leadCreationAction: LeadCreationAction;
  partnerId?: string;
}

const usePostBudgetCheckWithLead = ({
  locale,
  budgetCheckForm,
  onSuccess,
  leadCreationAction,
  partnerId,
}: UsePostApplicationWithLeadProps) => {
  const [form, setForm] = useState<Lead>();
  const saveLeadAfterApplication = useRef(false);
  const maximumBudgetRef = useRef<MaximumBudget>();

  const {
    isLoading: isLeadLoading,
    isError: isLeadError,
    mutate: postLead,
  } = usePostLead({
    onSuccess(lead) {
      onSuccess(maximumBudgetRef.current!, lead);
    },
    onError(error) {
      logger.error("Error while posting a lead.", error);
    },
  });

  const saveLead = useCallback(
    async (leadForm: Lead, maximumBudgetId: string) => {
      const utmOrigin = getOrigin();
      const origin = utmOrigin ? [utmOrigin] : [];
      const body: Lead = {
        email: leadForm.email,
        firstName: leadForm.firstName,
        lastName: leadForm.lastName,
        phone: leadForm.phone,
        language:
          locale === "immo" ? "DE" : (locale.toUpperCase() as LanguageISOCode),
        maximumBudgetId,
        origin,
        partnerId,
        action: leadCreationAction,
        wantsToSellAProperty:
          budgetCheckForm?.wantsToSellAProperty === YesNo.yes,
      };
      postLead({ body });
    },
    [budgetCheckForm, postLead, locale, leadCreationAction, partnerId],
  );

  const {
    mutate: postMaximumBudget,
    isLoading: isApplicationLoading,
    isError: isApplicationError,
  } = usePostMaximumBudget({
    onError(error, variables) {
      logger.error("Error while posting maximum budget.", error, variables);
    },
    onSuccess: (application) => {
      maximumBudgetRef.current = application;
      if (saveLeadAfterApplication.current) {
        saveLead(form!, maximumBudgetRef.current.id!);
      }
    },
  });

  useEffect(() => {
    if (!budgetCheckForm) {
      return;
    }

    const body = mapFormToMaximumBudget(budgetCheckForm, partnerId);

    postMaximumBudget({
      body: {
        ...body,
      },
    });
  }, [postMaximumBudget, budgetCheckForm, partnerId]);

  const handleSubmit = useCallback(
    async (form: Lead) => {
      setForm(form);
      if (maximumBudgetRef.current) {
        saveLead(form, maximumBudgetRef.current.id!);
      } else {
        saveLeadAfterApplication.current = true;
      }
    },
    [saveLead],
  );

  return {
    isLoading: (form && isApplicationLoading) || isLeadLoading,
    handleSubmit,
    isError: (form && isApplicationError) || isLeadError,
  };
};

export default usePostBudgetCheckWithLead;
